import {useEffect, useRef, useState} from "react";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {supplierApi} from "@/features/partner/supplierStatus/api/SupplierApi";
import {PartnerSettlement, partnerSettlementListFromDto} from "../types/partnerSettlement.types";
import {Supplier, supplierListFromDto} from "@/features/partner/supplierStatus/types/supplier.types";
import {partnerSettlementApi} from "../api/PartnerSettlementApi";
import { formatDateToDisplay } from "@/shared/utils/formatter";
import { buildExcelDownloadUrl } from "@/shared/utils/settlementApiHelpers";
import {useSettlementFile} from "../../../shared/hooks/useSettlementFile";

/**
 * 협력사 주정산내역 데이터 관리 훅
 */
export const usePartnerSettlementData = (userSupplierId?: string | null, skipInitialFetch = false) => {
    const { showToast } = useToast();
    const fileHelper = useSettlementFile('/api/v1/partner-settlement');
    const [settlements, setSettlements] = useState<PartnerSettlement[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [latestStartDate, setLatestStartDate] = useState<string>('');
    const [latestEndDate, setLatestEndDate] = useState<string>('');

    const hasFetchedSuppliers = useRef(false);
    const hasFetchedLatestDate = useRef(false);
    const hasFetchedInitialSettlements = useRef(false);

    // 협력사 목록 조회
    useEffect(() => {
        if (hasFetchedSuppliers.current) return;

        let isCancelled = false;

        const fetchSuppliers = async () => {
            try {
                const response = await supplierApi.getSupplierList();
                if (!isCancelled) {
                    const supplierList = supplierListFromDto(response);
                    setSuppliers(supplierList.suppliers);
                    hasFetchedSuppliers.current = true;
                }
            } catch {
                if (!isCancelled) {
                    showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                }
            }
        };

        fetchSuppliers();
        return () => {
            isCancelled = true;
        };
    }, [showToast]);

    // 최근 정산일자 조회 (시작일, 종료일)
    useEffect(() => {
        if (hasFetchedLatestDate.current) return;

        let isCancelled = false;

        const fetchLatestDate = async () => {
            try {
                const response = await partnerSettlementApi.getLatestSettlementDate();
                if (!isCancelled) {
                    // 배열 형태로 오는 경우 대비 포맷팅
                    const formattedStartDate = formatDateToDisplay(response.latestStartDate);
                    const formattedEndDate = formatDateToDisplay(response.latestEndDate);
                    setLatestStartDate(formattedStartDate);
                    setLatestEndDate(formattedEndDate);
                    hasFetchedLatestDate.current = true;
                }
            } catch {
                if (!isCancelled) {
                    // 에러가 발생해도 빈 문자열로 처리 (데이터가 없을 수 있음)
                    setLatestStartDate('');
                    setLatestEndDate('');
                    hasFetchedLatestDate.current = true;
                }
            }
        };

        fetchLatestDate();
        return () => {
            isCancelled = true;
        };
    }, [showToast]);

    // 최근 정산일자로 정산내역 조회 (초기 로딩 시에만, skipInitialFetch가 false일 때만)
    useEffect(() => {
        // skipInitialFetch가 true이면 자동 조회 건너뛰기
        if (skipInitialFetch) {
            hasFetchedInitialSettlements.current = true;
            return;
        }

        // suppliers가 로드되고 latestStartDate, latestEndDate가 있으며 아직 조회하지 않은 경우에만 실행
        if (!latestStartDate || !latestEndDate || hasFetchedInitialSettlements.current || suppliers.length === 0) return;

        let isCancelled = false;

        const fetchSettlements = async () => {
            try {
                // 협력사 사용자인 경우 해당 협력사 데이터만 조회
                const supplyId = userSupplierId || undefined;
                // 초기 조회 시 최근 정산시작일자와 정산종료일자로 설정
                const response = await partnerSettlementApi.getPartnerSettlementList(latestStartDate, latestEndDate, supplyId);
                if (!isCancelled) {
                    const settlementList = partnerSettlementListFromDto(response, suppliers);

                    setSettlements(settlementList);
                    hasFetchedInitialSettlements.current = true;
                }
            } catch {
                if (!isCancelled) {
                    showToast('정산내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                }
            }
        };

        fetchSettlements();
        return () => {
            isCancelled = true;
        };
    }, [latestStartDate, latestEndDate, showToast, suppliers, skipInitialFetch, userSupplierId]);

    // 모든 데이터가 로드되었는지 확인
    useEffect(() => {
        if (hasFetchedSuppliers.current && hasFetchedLatestDate.current) {
            setLoading(false);
        }
    }, [suppliers, latestStartDate, latestEndDate]);

    /**
     * 최신 정산일자 재조회
     */
    const refreshLatestDate = async (): Promise<{ startDate: string; endDate: string } | null> => {
        try {
            const response = await partnerSettlementApi.getLatestSettlementDate();
            // 배열 형태로 오는 경우 대비 포맷팅
            const formattedStartDate = formatDateToDisplay(response.latestStartDate);
            const formattedEndDate = formatDateToDisplay(response.latestEndDate);
            setLatestStartDate(formattedStartDate);
            setLatestEndDate(formattedEndDate);
            return { startDate: formattedStartDate, endDate: formattedEndDate };
        } catch {
            return null;
        }
    }

    /**
     * 정산내역 재조회
     * @returns 조회된 정산내역 건수 (에러 시 -1)
     */
    const refreshSettlements = async (acctStDate: string, acctEdDate: string, supplyId?: string): Promise<number> => {
        try {
            // Promise를 먼저 생성하여 병렬 처리 준비
            const apiPromise = partnerSettlementApi.getPartnerSettlementList(
                acctStDate,
                acctEdDate,
                supplyId
            );

            const response = await apiPromise;

            const settlementList = partnerSettlementListFromDto(response, suppliers);
            setSettlements(settlementList);
            return settlementList.length;
        } catch {
            showToast('정산내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            return -1;
        }
    };

    /**
     * 엑셀 업로드
     */
    const uploadExcel = async (file: File, userId: string): Promise<boolean> => {
        return fileHelper.uploadExcel(file, userId);
    };

    /**
     * 엑셀 다운로드
     */
    const downloadExcel = async (acctStDate: string, acctEdDate: string, supplyId?: string): Promise<void> => {
        try {
            setLoading(true);
            const url = buildExcelDownloadUrl('/api/v1/partner-settlement', {
                acctStDate,
                acctEdDate,
                supplyId: supplyId !== 'ALL' ? supplyId : undefined
            });

            const fileName = fileHelper.createSettlementFileName(
                settlements,
                { start: 'settlementStartDate', end: 'settlementEndDate' },
                '협력사정산내역',
                acctEdDate
            );

            await fileHelper.downloadExcel(url, () => fileName);
            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    return {
        settlements,
        suppliers,
        latestStartDate,
        latestEndDate,
        loading,
        refreshLatestDate,
        refreshSettlements,
        uploadExcel,
        downloadExcel
    };
};
